import { useCallback, useEffect, useMemo, useRef } from 'react';
import { endOfDay, format, isAfter, isBefore, parse, startOfDay } from 'date-fns';

import { Box, Table } from '@zillow/constellation-10';
import { LoadingConstants, numberUtils, SpinnerContainer } from '@zg-rentals/rental-manager-env';
import type { RentalManagerPaymentTransactionDto } from '@zg-rentals/ts-adproducts-client';

import BillingError from '../../components/BillingError';
import BillingEmptyBanner from '../../components/BillingEmptyBanner';
import { usePremiumPaymentHistory } from '../../hooks/usePremiumPaymentHistory';

const formatAddress = (transaction: RentalManagerPaymentTransactionDto) => {
  const { address } = transaction;
  if (!address) return '-';
  const parts = [address.street, address.unit].filter(Boolean);
  const cityState = [address.city, address.state].filter(Boolean).join(', ');
  if (cityState) parts.push(cityState);
  if (address.zip) parts.push(address.zip);
  return parts.join(' ') || '-';
};

const formatAmount = (price: string) => {
  const amount = parseFloat(price);
  return isNaN(amount) ? price : numberUtils.asCurrency(amount);
};

type PremiumPaymentsTableProps = {
  startDate?: string;
  endDate?: string;
};

const DATE_FORMAT = 'MM/dd/yyyy';

export const PremiumPaymentsTable = ({ startDate, endDate }: PremiumPaymentsTableProps) => {
  const { transactions, hasMoreData, loadingState, fetchNextPage, handleLoadMore } = usePremiumPaymentHistory();

  const filteredTransactions = useMemo(() => {
    if (!startDate && !endDate) return transactions;
    const start = startDate ? startOfDay(parse(startDate, DATE_FORMAT, new Date())) : undefined;
    const end = endDate ? endOfDay(parse(endDate, DATE_FORMAT, new Date())) : undefined;
    return transactions.filter((t) => {
      if (!t.datePaid) return false;
      const paid = new Date(t.datePaid);
      if (start && isBefore(paid, start)) return false;
      if (end && isAfter(paid, end)) return false;
      return true;
    });
  }, [transactions, startDate, endDate]);
  const initialLoadComplete =
    loadingState === LoadingConstants.SUCCESS || loadingState === LoadingConstants.ERROR || transactions.length > 0;
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchNextPage();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  const observerCallback = useCallback(
    (entries: Array<IntersectionObserverEntry>) => {
      const [entry] = entries;
      handleLoadMore(entry.isIntersecting);
    },
    [handleLoadMore],
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(observerCallback, { threshold: 0.1 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [observerCallback]);

  return (
    <div>
      {loadingState === LoadingConstants.ERROR && transactions.length === 0 && (
        <BillingError errorMsg="Unable to load premium payment history. Please try again later." />
      )}
      {initialLoadComplete ? (
        <>
          <Table.Root aria-label="Premium payments" appearance="horizontal" css={{ width: '100%' }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Date</Table.HeaderCell>
                <Table.HeaderCell>Transaction ID</Table.HeaderCell>
                <Table.HeaderCell>Property</Table.HeaderCell>
                <Table.HeaderCell css={{ textAlign: 'right' }}>Amount</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filteredTransactions.map((transaction) => (
                <Table.Row key={transaction.transactionId}>
                  <Table.Cell>
                    {transaction.datePaid ? format(new Date(transaction.datePaid), 'MM/dd/yy') : '-'}
                  </Table.Cell>
                  <Table.Cell>{transaction.transactionId}</Table.Cell>
                  <Table.Cell>{formatAddress(transaction)}</Table.Cell>
                  <Table.Cell css={{ textAlign: 'right' }}>{formatAmount(transaction.price)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
          {filteredTransactions.length === 0 && !hasMoreData && loadingState === LoadingConstants.SUCCESS && (
            <BillingEmptyBanner
              notification={{ title: 'No Premium Payments', text: ['No premium payment history found.'] }}
            />
          )}
          {hasMoreData && <div ref={sentinelRef} />}
          {loadingState === LoadingConstants.LOADING && transactions.length > 0 && (
            <Box css={{ paddingBlock: 'md', display: 'flex', justifyContent: 'center' }}>
              <SpinnerContainer />
            </Box>
          )}
        </>
      ) : (
        <SpinnerContainer />
      )}
    </div>
  );
};
